import { inject, Injectable, signal } from '@angular/core';
import { GameService } from '@core/game/game.service';
import { ProgressRecord, SAVE_SERVICE } from '@core/saves/saves.interface';
import { LpActionsService } from '@features/lp/lp-actions/lp-actions.service';
import { IConditionContext, LpConditionsService } from '@features/lp/lp-conditions/lp-conditions.service';
import { LpTriggersService } from '@features/lp/lp-triggers/lp-triggers.service';
import { LpActivity, LpActivityChange, LpMissionInfo } from '@typings/lp/generated';
import { Uid } from '@typings/id';

@Injectable({
  providedIn: 'root',
})
export class LpMissionService {
  public readonly missionQueue = signal<LpMissionInfo[]>([]);
  public readonly finishedMissions = signal<LpMissionInfo[]>([]);

  readonly #gameService = inject(GameService);
  readonly #lpActionsService = inject(LpActionsService);
  readonly #lpConditionsService = inject(LpConditionsService);
  readonly #lpTriggersService = inject(LpTriggersService);
  readonly #saveService = inject(SAVE_SERVICE);

  private unregisterMissionTrigger: (() => void) | undefined;
  private unregisterActivityTrigger: (() => void) | undefined;

  public async start(mission: LpMissionInfo) {
    if (this.#gameService.mission() !== undefined) {
      console.info(`Trying to start mission '${mission.name}' while another mission is still in progress. Queueing the mission.`);
      this.missionQueue.set([...this.missionQueue(), mission]);
      return;
    }
    await this.#gameService.setMission(mission.uid as Uid);
    await this.saveMissionStarted(mission);
    await this.#saveService.startPolling();

    this.unregisterMissionTrigger = await this.#lpTriggersService.on(mission.onTrigger);

    if (mission.onStart) {
      await this.#lpActionsService.perform(mission.onStart);
    }

    const startActivity = mission.mission.startActivity ?? mission.mission.activities[0];
    await this.startActivity(startActivity);
  }

  public async finish() {
    const mission = this.#gameService.mission();
    if (!mission) throw new Error('No mission to finish');

    await this.saveMissionFinished(mission);
    await this.#saveService.stopPolling();

    await this.#gameService.setMission(undefined);
    await this.#gameService.setActivity(undefined);
    this.unregisterMissionTrigger?.();
    this.finishedMissions.set([...this.finishedMissions(), mission]);

    const nextMission = this.missionQueue()[0];
    this.missionQueue.set(this.missionQueue().slice(1));
    if (nextMission) {
      await this.start(nextMission);
    }
  }

  public async startActivity(activity: LpActivity) {
    await this.#gameService.setActivity(activity.uid as Uid);
    await this.saveActivityStarted(activity);

    this.unregisterActivityTrigger = await this.#lpTriggersService.on(activity.onTrigger);

    if (activity.onStart) {
      await this.#lpActionsService.perform(activity.onStart);
    }

    if (activity.dataType === 'FinishMissionActivity') {
      if (activity.missionResult !== 'Success') {
        throw new Error(`Mission result '${activity.missionResult}' not supported`);
      }

      return await this.finishActivity(activity);
    }

    if (activity.dataType === 'RedirectActivity') {
      return await this.finishActivity(activity);
    }
  }

  public async finishActivity(activity: LpActivity, ctx?: IConditionContext) {
    if (activity.onFinish) {
      await this.#lpActionsService.perform(activity.onFinish);
    }
    await this.saveActivityFinished(activity);

    this.unregisterActivityTrigger?.();

    if (activity.dataType === 'FinishMissionActivity') {
      if (activity.missionResult !== 'Success') {
        throw new Error(`Mission result '${activity.missionResult}' not supported`);
      }

      await this.finish();
      return;
    }
    await this.change(activity.activityChange, ctx);
  }

  public async change(change: LpActivityChange, ctx?: IConditionContext) {
    // NOTE: try to keep the order of the if statements alphabetical

    if (change.dataType === 'ConditionalActivityChange') {
      for (const condition of change.conditions) {
        const satisfied = condition.condition ? await this.#lpConditionsService.evaluate(condition.condition, ctx) : true;
        if (satisfied) {
          await this.startActivity(condition.activity);
          return;
        }
      }

      if (change.otherwiseNextActivity) {
        await this.startActivity(change.otherwiseNextActivity);
      } else {
        throw new Error(
          `No next activity found for ConditionalActivityChange '${change.uid}'. No conditions were met, and no otherwiseNextActivity was defined`,
        );
      }
      return;
    }

    if (change.dataType === 'SimpleActivityChange') {
      if (!change.nextActivity) {
        throw new Error(`No next activity provided for SimpleActivityChange '${change.uid}'`); // TODO: this needs to not be optional
      }

      await this.startActivity(change.nextActivity);
      return;
    }

    console.warn(change);
    throw new Error(`Activity change '${change.dataType}' not supported`);
  }

  private async updateProgress<T extends 'activityProgress' | 'missionProgress'>(key: T, id: Uid, type: 'started' | 'finished') {
    const previousSave = await this.#saveService.load();
    const previousRecords = previousSave[key] as ProgressRecord[];

    const existingRecord = previousRecords.find((record) => record.uid === id);
    const timestampKey = type === 'started' ? 'started_timestamp' : 'finished_timestamp';

    if (!existingRecord?.[timestampKey]) {
      const updatedRecords = existingRecord
        ? previousRecords.map((record) =>
            record.uid === id
              ? {
                  ...record,
                  [timestampKey]: new Date().toISOString(),
                }
              : record,
          )
        : [
            ...previousRecords,
            {
              uid: id,
              [timestampKey]: new Date().toISOString(),
            },
          ];

      const newSave = {
        ...previousSave,
        [key]: updatedRecords,
      };
      await this.#saveService.save(newSave);
    }
  }

  private async saveActivityStarted(activity: LpActivity) {
    await this.updateProgress('activityProgress', activity.uid as Uid, 'started');
  }

  private async saveActivityFinished(activity: LpActivity) {
    await this.updateProgress('activityProgress', activity.uid as Uid, 'finished');
  }

  private async saveMissionStarted(mission: LpMissionInfo) {
    await this.updateProgress('missionProgress', mission.uid as Uid, 'started');
  }

  private async saveMissionFinished(mission: LpMissionInfo) {
    await this.updateProgress('missionProgress', mission.uid as Uid, 'finished');
  }
}
